import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';

interface Question {
  text: string;
  type: 'MultipleChoice' | 'TrueFalse';
  answers: {
    text: string;
    isCorrect: boolean;
  }[];
}

interface LessonFile {
  fileName: string;
  fileUrl: string;
  order: number;
}

interface LessonData {
  id: number;
  lessonName: string;
  titel?: string;
  type: string;
  videoUrl?: string;
  attachmentUrl?: string;
  attachmentTitel?: string;
  active: boolean;
  order: number;
  unitId: number;
  questions?: Question[];
  files?: LessonFile[];
  isNavigationEnabled?: boolean;
}

interface QuizLesson {
  id: number;
  lessonName: string;
  unitId: number;
}

interface ImportCourseSummary {
  id: string;
  name: string;
}

interface Unit {
  id: number;
  title: string;
  lessons: Lesson[];
  isVisible: boolean;
}

interface Lesson {
  id: number;
  title: string;
  type: 'video' | 'quiz' | 'file' | 'assignment';
  isVisible: boolean;
  order?: number;
}

const LessonView = () => {
  const { lessonId } = useParams<{ courseId?: string; unitId?: string; lessonId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lessonData, setLessonData] = useState<LessonData | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isEditing, setIsEditing] = useState(true);
  const [editedData, setEditedData] = useState<Partial<LessonData>>({});
  const [isNavigationEnabled, setIsNavigationEnabled] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Question>({
    text: '',
    type: 'MultipleChoice',
    answers: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]
  });
  const [lessonFiles, setLessonFiles] = useState<LessonFile[]>([]);
  const [newFileName, setNewFileName] = useState('');
  const [newFileUrl, setNewFileUrl] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCourses, setImportCourses] = useState<ImportCourseSummary[]>([]);
  const [importCoursesLoading, setImportCoursesLoading] = useState(false);
  const [selectedImportCourseId, setSelectedImportCourseId] = useState<string>('');
  const [importUnits, setImportUnits] = useState<Unit[]>([]);
  const [selectedImportUnitId, setSelectedImportUnitId] = useState<number | null>(null);
  const [importQuizzes, setImportQuizzes] = useState<Lesson[]>([]);
  const [selectedImportQuizId, setSelectedImportQuizId] = useState<number | null>(null);
  const [isLoadingImport, setIsLoadingImport] = useState(false);

  // Handle input changes
  const handleInputChange = (field: keyof LessonData, value: string | number) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save changes
  const handleSave = async () => {
    if (!lessonData) return;

    try {
      const currentQuestions = questions.length > 0 ? questions : (lessonData.questions || []);
      const currentFiles = lessonFiles.length > 0 ? lessonFiles : (lessonData.files || []);
      
      const payload: any = {
        lessonName: editedData.lessonName || lessonData.lessonName,
        titel: editedData.lessonName || lessonData.lessonName, // Using lessonName as titel
        unitId: lessonData.unitId,
        active: lessonData.active,
        order: editedData.order || lessonData.order,
        type: lessonData.type,
        videoUrl: editedData.videoUrl || lessonData.videoUrl,
        attachmentTitel: editedData.attachmentTitel || lessonData.attachmentTitel,
        attachmentUrl: editedData.attachmentUrl || lessonData.attachmentUrl,
        questions: currentQuestions
      };

      // Add navigation enabled for Video type lessons
      if (lessonData.type.toLowerCase() === 'video' || lessonData.type === 'Video') {
        payload.isNavigationEnabled = isNavigationEnabled;
      }

      // Add files for File type lessons
      if (lessonData.type.toLowerCase() === 'file' || lessonData.type === 'File') {
        payload.files = currentFiles;
      }
      
      const response = await authFetch(apiUrl(`Lesson/${lessonId}`), {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        setSaveMessage({ type: 'success', text: 'تم حفظ التغييرات بنجاح!' });
        setLessonData(prev => ({
          ...prev!,
          ...editedData,
          questions: currentQuestions,
          files: currentFiles
        }));
        setIsEditing(false);
        setEditedData({});

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'فشل في حفظ التغييرات');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setSaveMessage({ type: 'error', text: 'فشل في حفظ التغييرات. يرجى المحاولة مرة أخرى.' });
    }
  };

  // Function to add a new question
  const handleAddQuestion = () => {
    if (newQuestion.text.trim() === '') {
      setSaveMessage({ type: 'error', text: 'نص السؤال لا يمكن أن يكون فارغاً' });
      return;
    }

    // For TrueFalse questions, ensure there are exactly 2 answers: True and False
    let processedQuestion = { ...newQuestion };
    if (newQuestion.type === 'TrueFalse') {
      processedQuestion.answers = [
        { text: 'True', isCorrect: newQuestion.answers.find(a => a.isCorrect)?.text === 'True' || false },
        { text: 'False', isCorrect: newQuestion.answers.find(a => a.isCorrect)?.text === 'False' || false }
      ];
    } else {
      // For MultipleChoice, filter out empty answers
      processedQuestion.answers = newQuestion.answers.filter(a => a.text.trim() !== '');
      
      // Ensure at least one answer is marked as correct
      if (!processedQuestion.answers.some(a => a.isCorrect)) {
        setSaveMessage({ type: 'error', text: 'يجب تحديد إجابة واحدة على الأقل كصحيحة' });
        return;
      }
    }

    // Add the new question to the questions array
    const updatedQuestions = [...questions, processedQuestion];
    setQuestions(updatedQuestions);
    
    // Reset form
    setNewQuestion({
      text: '',
      type: 'MultipleChoice',
      answers: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ]
    });
    
    setShowQuestionForm(false);
    setSaveMessage({ type: 'success', text: 'تم إضافة السؤال بنجاح!' });
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSaveMessage(null);
    }, 3000);
  };

  // Handle question input changes
  const handleQuestionChange = (field: keyof Question, value: string | 'MultipleChoice' | 'TrueFalse') => {
    setNewQuestion(prev => ({
      ...prev,
      [field]: value
    }));

    // If type changes to TrueFalse, reset answers
    if (field === 'type' && value === 'TrueFalse') {
      setNewQuestion(prev => ({
        ...prev,
        answers: [
          { text: 'True', isCorrect: false },
          { text: 'False', isCorrect: false }
        ]
      }));
    } else if (field === 'type' && value === 'MultipleChoice') {
      setNewQuestion(prev => ({
        ...prev,
        answers: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false }
        ]
      }));
    }
  };

  // Handle answer changes
  const handleAnswerChange = (index: number, field: 'text' | 'isCorrect', value: string | boolean) => {
    setNewQuestion(prev => {
      const updatedAnswers = [...prev.answers];
      updatedAnswers[index] = {
        ...updatedAnswers[index],
        [field]: value
      };

      // For TrueFalse questions, ensure only one answer is correct
      if (prev.type === 'TrueFalse' && field === 'isCorrect' && value === true) {
        updatedAnswers.forEach((answer, i) => {
          if (i !== index) {
            updatedAnswers[i] = { ...answer, isCorrect: false };
          }
        });
      }

      return {
        ...prev,
        answers: updatedAnswers
      };
    });
  };

  // Handle removing an answer from new question
  const handleRemoveAnswer = (index: number) => {
    setNewQuestion(prev => ({
      ...prev,
      answers: prev.answers.filter((_, i) => i !== index)
    }));
  };

  // Handle adding an answer to new question
  const handleAddAnswer = () => {
    setNewQuestion(prev => ({
      ...prev,
      answers: [...prev.answers, { text: '', isCorrect: false }]
    }));
  };

  // Function to update existing question text
  const handleUpdateQuestionText = (questionIndex: number, text: string) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      text
    };
    setQuestions(updatedQuestions);
  };

  // Function to update existing answer (text and/or isCorrect)
  const handleUpdateExistingAnswer = (
    questionIndex: number,
    answerIndex: number,
    field: 'text' | 'isCorrect',
    value: string | boolean
  ) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const updatedAnswers = [...question.answers];
    updatedAnswers[answerIndex] = {
      ...updatedAnswers[answerIndex],
      [field]: value
    };

    // For TrueFalse: when marking one correct, unmark the other
    if (question.type === 'TrueFalse' && field === 'isCorrect' && value === true) {
      updatedAnswers.forEach((answer, i) => {
        if (i !== answerIndex) {
          updatedAnswers[i] = { ...answer, isCorrect: false };
        }
      });
    }

    updatedQuestions[questionIndex] = {
      ...question,
      answers: updatedAnswers
    };
    setQuestions(updatedQuestions);
  };

  // Function to remove an answer from existing question
  const handleRemoveExistingAnswer = (questionIndex: number, answerIndex: number) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    updatedQuestions[questionIndex] = {
      ...question,
      answers: question.answers.filter((_, i) => i !== answerIndex)
    };
    setQuestions(updatedQuestions);
  };

  // Function to add an answer to existing question
  const handleAddExistingAnswer = (questionIndex: number) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    updatedQuestions[questionIndex] = {
      ...question,
      answers: [...question.answers, { text: '', isCorrect: false }]
    };
    setQuestions(updatedQuestions);
  };

  // Function to delete a question
  const handleDeleteQuestion = (questionIndex: number) => {
    if (window.confirm('هل أنت متأكد من حذف هذا السؤال؟')) {
      const updatedQuestions = questions.filter((_, index) => index !== questionIndex);
      setQuestions(updatedQuestions);
      setSaveMessage({ type: 'success', text: 'تم حذف السؤال. انقر على حفظ لتطبيق التغييرات.' });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  // Function to add a new file
  const handleAddFile = () => {
    if (!newFileName.trim() || !newFileUrl.trim()) {
      setSaveMessage({ type: 'error', text: 'يرجى ملء اسم الملف ورابط الملف' });
      return;
    }

    const newFile: LessonFile = {
      fileName: newFileName,
      fileUrl: newFileUrl,
      order: lessonFiles.length + 1
    };

    setLessonFiles([...lessonFiles, newFile]);
    setNewFileName('');
    setNewFileUrl('');
    setSaveMessage({ type: 'success', text: 'تم إضافة الملف. انقر على حفظ لتطبيق التغييرات.' });
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSaveMessage(null);
    }, 3000);
  };

  // Function to delete a file
  const handleDeleteFile = (fileIndex: number) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الملف؟')) {
      const updatedFiles = lessonFiles.filter((_, index) => index !== fileIndex).map((file, i) => ({
        ...file,
        order: i + 1
      }));
      setLessonFiles(updatedFiles);
      setSaveMessage({ type: 'success', text: 'تم حذف الملف. انقر على حفظ لتطبيق التغييرات.' });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  // Function to fetch available courses for import
  const fetchImportCourses = async () => {
    setImportCoursesLoading(true);
    try {
      const response = await authFetch(
        apiUrl('Course/all?pageNumber=1&pageSize=100'),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch courses for import');
      }

      const data = await response.json();

      if (data.success && data.data && Array.isArray(data.data.data)) {
        const courseData = data.data;
        const formatted: ImportCourseSummary[] = courseData.data.map((course: any) => ({
          id: course.id,
          name: course.courseName
        }));
        setImportCourses(formatted);
      } else {
        setImportCourses([]);
      }
    } catch (error) {
      console.error('Error fetching import courses:', error);
      setImportCourses([]);
      setSaveMessage({ type: 'error', text: 'فشل في تحميل الدورات المتاحة' });
    } finally {
      setImportCoursesLoading(false);
    }
  };

  // Function to fetch units for selected course
  const fetchImportUnitsForCourse = async (courseIdForImport: string) => {
    setImportUnits([]);
    setSelectedImportUnitId(null);
    setImportQuizzes([]);
    setSelectedImportQuizId(null);

    if (!courseIdForImport) return;

    try {
      const response = await authFetch(apiUrl(`Course/tree?courseid=${courseIdForImport}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success && data.data && Array.isArray(data.data.units)) {
        const mappedUnits: Unit[] = data.data.units.map((unit: any) => ({
          id: unit.id,
          title: unit.unitName || unit.titel || unit.title,
          lessons: (unit.lessons || []).map((lesson: any) => ({
            id: lesson.id,
            title: lesson.lessonName || lesson.titel || lesson.title,
            type:
              lesson.type === 'Video'
                ? 'video'
                : lesson.type === 'File'
                ? 'file'
                : lesson.type === 'Assignment'
                ? 'assignment'
                : 'quiz',
            isVisible: lesson.active,
            order: lesson.order
          })),
          isVisible: unit.active
        }));
        setImportUnits(mappedUnits);
      } else {
        setImportUnits([]);
      }
    } catch (error) {
      console.error('Error fetching units for course:', error);
      setImportUnits([]);
    }
  };

  // Function to import questions from selected quiz
  const handleImportQuestions = async () => {
    if (!selectedImportCourseId || !selectedImportUnitId || !selectedImportQuizId) {
      setSaveMessage({ 
        type: 'error', 
        text: 'يرجى اختيار الدورة والوحدة والاختبار المراد استيراده' 
      });
      return;
    }

    try {
      setIsLoadingImport(true);
      const response = await authFetch(apiUrl(`Lesson/${selectedImportQuizId}`), {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const result = await response.json();
      if (result.success && result.data.questions && Array.isArray(result.data.questions)) {
        const importedQuestions = result.data.questions;
        
        if (importedQuestions.length === 0) {
          setSaveMessage({ type: 'error', text: 'لا توجد أسئلة في الاختبار المحدد' });
          return;
        }

        // Add imported questions to current questions
        setQuestions([...questions, ...importedQuestions]);
        setShowImportModal(false);
        setSelectedImportCourseId('');
        setSelectedImportUnitId(null);
        setSelectedImportQuizId(null);
        setSaveMessage({ 
          type: 'success', 
          text: `تم استيراد ${importedQuestions.length} سؤال بنجاح. انقر على حفظ لتطبيق التغييرات.` 
        });

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        throw new Error('فشل في استيراد الأسئلة');
      }
    } catch (error) {
      console.error('Error importing questions:', error);
      setSaveMessage({ type: 'error', text: 'فشل في استيراد الأسئلة. يرجى المحاولة مرة أخرى.' });
    } finally {
      setIsLoadingImport(false);
    }
  };

  // Open import modal and fetch available courses
  const handleOpenImportModal = () => {
    setShowImportModal(true);
    fetchImportCourses();
  };

  useEffect(() => {
    const fetchLessonData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('Fetching lesson data for ID:', lessonId);
        const lessonResponse = await authFetch(apiUrl(`Lesson/${lessonId}`), {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!lessonResponse.ok) {
          throw new Error(`HTTP error! status: ${lessonResponse.status}`);
        }

        const responseText = await lessonResponse.text();
        console.log('Raw response:', responseText);

        let lessonResult;
        try {
          lessonResult = JSON.parse(responseText);
        } catch (e) {
          console.error('JSON parse error:', e);
          throw new Error('Invalid JSON response from server');
        }

        if (!lessonResult.success) {
          throw new Error(lessonResult.message || 'فشل في جلب بيانات الدرس');
        }

        console.log('Parsed lesson data:', lessonResult.data);

        // Normalize the data
        const normalizedData = {
          ...lessonResult.data,
          type: (lessonResult.data.type || '').toLowerCase(),
          questions: Array.isArray(lessonResult.data.questions) ? lessonResult.data.questions : [],
          files: Array.isArray(lessonResult.data.files) ? lessonResult.data.files : []
        };

        console.log('Normalized data:', normalizedData);
        setLessonData(normalizedData);
        
        // Only set questions if they exist and are in the correct format
        if (Array.isArray(normalizedData.questions) && normalizedData.questions.length > 0) {
          setQuestions(normalizedData.questions);
        }

        // Set files if they exist
        if (Array.isArray(normalizedData.files) && normalizedData.files.length > 0) {
          setLessonFiles(normalizedData.files);
        }

        // Set navigation enabled for video lessons
        if (normalizedData.type.toLowerCase() === 'video') {
          setIsNavigationEnabled(normalizedData.isNavigationEnabled || false);
        }

      } catch (error) {
        console.error('Error in fetchLessonData:', error);
        setError(error instanceof Error ? error.message : 'فشل في تحميل بيانات الدرس. يرجى المحاولة مرة أخرى لاحقاً.');
      } finally {
        setLoading(false);
      }
    };

    if (lessonId) {
      fetchLessonData();
    }
  }, [lessonId]);

  // Update list of available quizzes in selected import unit
  useEffect(() => {
    if (!selectedImportUnitId) {
      setImportQuizzes([]);
      setSelectedImportQuizId(null);
      return;
    }

    const unit = importUnits.find(u => u.id === selectedImportUnitId);
    if (unit && unit.lessons) {
      setImportQuizzes(unit.lessons.filter(lesson => lesson.type === 'quiz'));
    } else {
      setImportQuizzes([]);
    }
    setSelectedImportQuizId(null);
  }, [selectedImportUnitId, importUnits]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex justify-center items-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !lessonData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <ArrowLeftIcon className="h-5 w-5 ml-2" />
              رجوع
            </button>
          </div>
          <div className="text-center text-red-600">
            {error || 'لم يتم العثور على بيانات الدرس'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <ArrowLeftIcon className="h-5 w-5 ml-2" />
            رجوع
          </button>
          {isEditing ? (
            <div className="space-x-reverse space-x-2">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                حفظ
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedData({});
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                إلغاء
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              تعديل
            </button>
          )}
        </div>

        {/* Save message */}
        {saveMessage && (
          <div
            className={`mb-4 p-3 rounded ${
              saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        {/* Lesson details */}
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold mb-2">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.lessonName || lessonData.lessonName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('lessonName', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                lessonData.lessonName
                  )}
            </h1>
                </div>

          {/* Video content */}
          {(lessonData.type.toLowerCase() === 'video' || lessonData.type === 'Video') && (
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رابط الفيديو
                  </label>
                    <input
                    type="text"
                    value={editedData.videoUrl || lessonData.videoUrl || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('videoUrl', e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="أدخل رابط فيديو يوتيوب"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="videoNavigationEnabled"
                    type="checkbox"
                    checked={isNavigationEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsNavigationEnabled(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={!isEditing}
                  />
                  <label htmlFor="videoNavigationEnabled" className="text-sm font-medium text-gray-700">
                    السماح بالانتقال إلى الدرس على اليوتيوب
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* File lesson content */}
          {(lessonData.type.toLowerCase() === 'file' || lessonData.type === 'File') && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">الملفات</h2>
              
              {/* Files List */}
              {lessonFiles.length > 0 && (
                <div className="space-y-4">
                  {lessonFiles
                    .sort((a, b) => a.order - b.order)
                    .map((file, fileIndex) => (
                    <div key={fileIndex} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-reverse space-x-3">
                            <div className="flex-shrink-0">
                              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-medium text-gray-900">{file.fileName}</h3>
                              <a
                                href={file.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm mt-1 inline-block"
                              >
                                {file.fileUrl}
                              </a>
                            </div>
                          </div>
                        </div>
                        {isEditing && (
                          <button 
                            onClick={() => handleDeleteFile(fileIndex)}
                            className="text-red-500 hover:text-red-700"
                            title="حذف الملف"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add File Form when editing */}
              {isEditing && (
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">إضافة ملف جديد</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        اسم الملف
                      </label>
                      <input
                        type="text"
                        value={newFileName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFileName(e.target.value)}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="مثال: document1.pdf"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        رابط الملف
                      </label>
                      <input
                        type="url"
                        value={newFileUrl}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFileUrl(e.target.value)}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/files/doc1.pdf"
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-reverse space-x-2 pt-4">
                      <button
                        onClick={() => {
                          setNewFileName('');
                          setNewFileUrl('');
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={handleAddFile}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        إضافة ملف
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {lessonFiles.length === 0 && !isEditing && (
                <div className="text-center text-gray-500 py-8">
                  لا توجد ملفات متاحة لهذا الدرس.
                </div>
              )}
            </div>
          )}

          {/* Quiz content */}
          {(lessonData.type.toLowerCase() === 'quiz' || lessonData.type === 'Quiz') && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">أسئلة الاختبار</h2>
                {isEditing && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleOpenImportModal}
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center"
                    >
                      <span className="ml-1">⬇</span> استيراد أسئلة
                    </button>
                    <button
                      onClick={() => setShowQuestionForm(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                    >
                      <span className="ml-1">+</span> إضافة سؤال
                    </button>
                  </div>
                )}
              </div>
              
              {/* Question Form */}
              {showQuestionForm && isEditing && (
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">إضافة سؤال جديد</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نص السؤال
                      </label>
                      <input
                        type="text"
                        value={newQuestion.text}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuestionChange('text', e.target.value)}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="أدخل سؤالك"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع السؤال
                      </label>
                      <select
                        value={newQuestion.type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleQuestionChange('type', e.target.value as 'MultipleChoice' | 'TrueFalse')}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="MultipleChoice">اختيار متعدد</option>
                        <option value="TrueFalse">صح/خطأ</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الإجابات
                      </label>
                      <div className="space-y-2">
                        {newQuestion.answers.map((answer, index) => (
                          <div key={index} className="flex items-center space-x-reverse space-x-2">
                            <input
                              type="text"
                              value={answer.text}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(index, 'text', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder={`إجابة ${index + 1}`}
                              disabled={newQuestion.type === 'TrueFalse'}
                            />
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={answer.isCorrect}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(index, 'isCorrect', e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="mr-2 text-sm text-gray-600">صحيح</span>
                            </div>
                            {newQuestion.type === 'MultipleChoice' && newQuestion.answers.length > 2 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveAnswer(index)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="حذف الإجابة"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {newQuestion.type === 'MultipleChoice' && (
                        <button
                          type="button"
                          onClick={handleAddAnswer}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          + إضافة إجابة
                        </button>
                      )}
                    </div>
                    
                    <div className="flex justify-end space-x-reverse space-x-2 pt-4">
                      <button
                        onClick={() => setShowQuestionForm(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={handleAddQuestion}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        إضافة سؤال
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Existing Questions */}
              {questions.map((question, questionIndex) => (
                <div key={questionIndex} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                  <div className="mb-4 flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        سؤال {questionIndex + 1}
                      </h3>
                      {isEditing ? (
                        <input
                          type="text"
                          value={question.text}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateQuestionText(questionIndex, e.target.value)}
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="نص السؤال"
                        />
                      ) : (
                        <p className="mt-2 text-gray-700">{question.text}</p>
                      )}
                    </div>
                    {isEditing && (
                      <button 
                        onClick={() => handleDeleteQuestion(questionIndex)}
                        className="text-red-500 hover:text-red-700 mr-4"
                        title="حذف السؤال"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      الإجابات
                    </label>
                    {question.answers.map((answer, answerIndex) => (
                      <div 
                        key={answerIndex}
                        className={`p-3 rounded-lg border ${
                          answer.isCorrect 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 flex-shrink-0">
                            {String.fromCharCode(65 + answerIndex)}
                          </span>
                          {isEditing ? (
                            <>
                              <input
                                type="text"
                                value={answer.text}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateExistingAnswer(questionIndex, answerIndex, 'text', e.target.value)}
                                className={`flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${answer.isCorrect ? 'font-medium' : ''}`}
                                placeholder={`إجابة ${answerIndex + 1}`}
                                disabled={question.type === 'TrueFalse'}
                              />
                              <div className="flex items-center flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={answer.isCorrect}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateExistingAnswer(questionIndex, answerIndex, 'isCorrect', e.target.checked)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="mr-2 text-sm text-gray-600">صحيح</span>
                              </div>
                              {question.type === 'MultipleChoice' && question.answers.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveExistingAnswer(questionIndex, answerIndex)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="حذف الإجابة"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              <span className={`flex-1 ${answer.isCorrect ? 'font-medium' : ''}`}>
                                {answer.text}
                              </span>
                              {answer.isCorrect && (
                                <span className="text-green-600">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    {isEditing && question.type === 'MultipleChoice' && (
                      <button
                        type="button"
                        onClick={() => handleAddExistingAnswer(questionIndex)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + إضافة إجابة
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {questions.length === 0 && !showQuestionForm && (
                <div className="text-center text-gray-500 py-8">
                  لا توجد أسئلة متاحة لهذا الاختبار.
                  {isEditing && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowQuestionForm(true)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        أضف السؤال الأول
                      </button>
                    </div>
                  )}
                </div>
              )}
                    </div>
          )}

          {/* Attachment section */}
          {lessonData.attachmentUrl && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-2">المرفق</h3>
              <a
                href={lessonData.attachmentUrl}
                      target="_blank" 
                      rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <span className="ml-2">📎</span>
                {lessonData.attachmentTitel || 'تحميل المرفق'}
              </a>
            </div>
          )}

          {/* Attachment inputs when editing */}
          {isEditing && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان المرفق
                </label>
                      <input
                        type="text"
                  value={editedData.attachmentTitel || lessonData.attachmentTitel || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('attachmentTitel', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل عنوان المرفق"
                      />
                    </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رابط المرفق
                </label>
                      <input
                  type="text"
                        value={editedData.attachmentUrl || lessonData.attachmentUrl || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('attachmentUrl', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل رابط المرفق"
                      />
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Import Questions Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">استيراد أسئلة من اختبار موجود (اختياري)</h3>
            <p className="text-sm text-gray-600 mb-4">
              يمكنك اختيار دورة ووحدة واختبار سبق إنشاؤه لنسخ الأسئلة إلى هذا الاختبار الجديد.
            </p>
            
            {isLoadingImport && !importCoursesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Course Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اختر الدورة:
                    </label>
                    <select
                      value={selectedImportCourseId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedImportCourseId(value);
                        fetchImportUnitsForCourse(value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={importCoursesLoading}
                    >
                      <option value="">
                        {importCoursesLoading ? 'جاري تحميل الدورات...' : 'اختر الدورة'}
                      </option>
                      {importCourses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Unit Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اختر الوحدة (من الدورة المحددة):
                    </label>
                    <select
                      value={selectedImportUnitId || ''}
                      onChange={(e) => setSelectedImportUnitId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!selectedImportCourseId || importUnits.length === 0}
                    >
                      <option value="">اختر الوحدة</option>
                      {importUnits.map(unit => (
                        <option key={unit.id} value={unit.id}>
                          {unit.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Quiz Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اختر الاختبار (الدرس):
                    </label>
                    <select
                      value={selectedImportQuizId || ''}
                      onChange={(e) => setSelectedImportQuizId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!selectedImportUnitId || importQuizzes.length === 0}
                    >
                      <option value="">اختر الاختبار</option>
                      {importQuizzes.map(lesson => (
                        <option key={lesson.id} value={lesson.id}>
                          {lesson.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowImportModal(false);
                      setSelectedImportCourseId('');
                      setSelectedImportUnitId(null);
                      setSelectedImportQuizId(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleImportQuestions}
                    disabled={
                      isLoadingImport ||
                      !selectedImportCourseId ||
                      !selectedImportUnitId ||
                      !selectedImportQuizId
                    }
                    className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
                      isLoadingImport ||
                      !selectedImportCourseId ||
                      !selectedImportUnitId ||
                      !selectedImportQuizId
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                  >
                    {isLoadingImport ? 'جاري استيراد الأسئلة...' : 'استيراد الأسئلة من اختبار'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonView; 